import { z } from "zod";

import { Intent } from "@/shared/agent-prompts/workflow-types";
import { ClosedReason } from "@/src/models/comms/enums";
import { DatabaseDefaults } from "@/src/models/database-defaults";

/**
 * Lifecycle state of a patient's lifelong SMS conversation.
 *
 * Stored as the current-state projection on `sms_conversation.status`; the full
 * history of transitions lives in `sms_conversation_event`. "opened",
 * "un-escalated", and "reopened" are transitions (from_status -> to_status), not
 * terminal states, so they are not enumerated here.
 *
 * Superset of the legacy `ThreadStatus` (processing / awaiting_information /
 * cancelled / completed / escalated / failed), plus the conversation-only `open`
 * and `abandoned` states, so a conversation can carry the same status a thread
 * would.
 */
export enum SmsConversationStatus {
  PROCESSING = "processing",
  AWAITING_INFORMATION = "awaiting_information",
  ESCALATED = "escalated",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  ABANDONED = "abandoned",
  FAILED = "failed",
}

/** SMS-agent settings projected off a conversation (agent on/off + auto-send cohort). */
export type SmsAgentSettings = {
  agentEnabled: boolean;
  autoSend: boolean;
};

/** Defaults for a patient with no conversation yet: agent on, draft -> human review. */
export const DEFAULT_SMS_AGENT_SETTINGS: SmsAgentSettings = {
  agentEnabled: true,
  autoSend: false,
};

/** Statuses that close out an interaction (a terminal state). */
export const terminalSmsConversationStatuses = [
  SmsConversationStatus.COMPLETED,
  SmsConversationStatus.CANCELLED,
  SmsConversationStatus.ABANDONED,
  SmsConversationStatus.FAILED,
];

/**
 * Statuses a human operator may set directly from the inbox (e.g. the Complete / Reopen buttons).
 * Everything else is agent- or system-driven, so a manual transition request that targets a status
 * outside this set is rejected at the boundary (fail closed).
 */
export const userSettableSmsConversationStatuses = [SmsConversationStatus.COMPLETED, SmsConversationStatus.PROCESSING];

/** Selectable label for one manual close reason in the inbox Complete dialog. */
export type ManualCloseReasonOption = { value: ClosedReason; label: string; annotation?: string };

/**
 * Reasons a human operator may attach when completing (closing) a conversation from the inbox.
 * Recorded as the `reason` on the close transition event so the history explains why it was closed.
 * `APPOINTMENT_BOOKED` also feeds appointment attribution (see `getAppointmentBookedClosuresByPatientId`).
 */
export const MANUAL_CLOSE_REASON_OPTIONS: ManualCloseReasonOption[] = [
  { value: ClosedReason.APPOINTMENT_BOOKED, label: "Appointment booked outside of Valerie" },
  {
    value: ClosedReason.APPOINTMENT_BOOKED_BY_VALERIE,
    label: "Appointment booked by Valerie",
    annotation:
      "Only mark this if YOU manually booked the appointment. If the agent booked the appointment, but you closed out the conversation, please select Other.",
  },
  { value: ClosedReason.ABANDONED, label: "Abandoned" },
  { value: ClosedReason.OTHER, label: "Other" },
];

/** The set of reasons accepted on a manual close (used to validate the transition request). */
export const manualCloseReasons = MANUAL_CLOSE_REASON_OPTIONS.map((option) => option.value);

/** Whether `reason` is a valid manual close reason. */
export function isManualCloseReason(reason: ClosedReason): boolean {
  return manualCloseReasons.includes(reason);
}

/** Selectable label for one workflow intent an operator can attach when reopening a conversation. */
export type ReopenIntentOption = { value: Intent; label: string };

/**
 * Workflow intents a human operator may attach when reopening a conversation from the inbox. The
 * chosen intent is passed to the transition writer, which opens/resumes that intent's workflow so the
 * reopened conversation is scoped to what it's about.
 */
export const REOPEN_INTENT_OPTIONS: ReopenIntentOption[] = [
  { value: Intent.Faq, label: "FAQ" },
  { value: Intent.ScheduleInitial, label: "Schedule Initial" },
];

/** The set of intents accepted on a manual reopen (used to validate the transition request). */
export const reopenIntents = REOPEN_INTENT_OPTIONS.map((option) => option.value);

/** Whether `intent` is a valid manual reopen intent. */
export function isReopenIntent(intent: Intent): boolean {
  return reopenIntents.includes(intent);
}

/** Whether `status` represents a closed-out (terminal) conversation. Null (no conversation) is not terminal. */
export function isTerminalSmsConversationStatus(status: SmsConversationStatus | null): boolean {
  return status !== null && terminalSmsConversationStatuses.includes(status);
}

/** Whether `status` is sticky-terminal: once reached, no later inbound message may ever resume the conversation. */
export function isStickyTerminalSmsConversationStatus(status: SmsConversationStatus | null): boolean {
  return status === SmsConversationStatus.CANCELLED;
}

export const smsConversationSchema = z.object({
  patientId: z.string().uuid(),
  // Current lifecycle state; projection over sms_conversation_event.
  // Escalation is represented by status === ESCALATED (no separate flag).
  status: z.nativeEnum(SmsConversationStatus),
  // Why staff were paged; set while escalated, cleared otherwise.
  escalationReason: z.string().nullable(),
  // When `status` last changed.
  lastStatusAt: z.string(),
  // SMS agent on/off for this patient.
  agentEnabled: z.boolean(),
  // Fully-agentic (auto-send) vs draft -> human review (HITL).
  autoSend: z.boolean(),
  // Staff user who owns this conversation's inbox.
  assignedToUserId: z.string().uuid().nullable(),
  // When the current assignment was made.
  assignedAt: z.string().nullable(),
  // Who last changed state/settings.
  updatedByUserId: z.string().uuid().nullable(),
  // The workflow the most recent message operated on; null when idle with no workflow yet.
  workflowId: z.string().uuid().nullable(),
});

export type SmsConversation = z.infer<typeof smsConversationSchema> & DatabaseDefaults;

export const smsConversationShape: SmsConversation = {
  id: "",
  groupId: "",
  patientId: "",
  status: SmsConversationStatus.PROCESSING,
  escalationReason: null,
  lastStatusAt: "",
  agentEnabled: true,
  autoSend: false,
  assignedToUserId: null,
  assignedAt: null,
  updatedByUserId: null,
  workflowId: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
};
